import type React from 'react';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface FadeInProps {
	children: React.ReactNode;
	/** Milliseconds to wait before starting the opacity transition (default 150ms) */
	delay?: number;
	className?: string;
}

/**
 * Renders children at opacity-0 (via CSS class) and transitions to opacity-100
 * after `delay` ms. Remounts — and re-triggers the fade — whenever its `key`
 * prop changes. The CSS class ensures the element is invisible on the very
 * first browser paint, before JavaScript runs.
 */
const FadeIn = ({ children, delay = 150, className }: FadeInProps): React.JSX.Element => {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const id = setTimeout(() => setVisible(true), delay);
		return () => clearTimeout(id);
	}, [delay]);

	return (
		<div
			className={cn(
				'transition-opacity duration-200',
				visible ? 'opacity-100' : 'opacity-0',
				className
			)}
		>
			{children}
		</div>
	);
};

export default FadeIn;
